import React, { useState, useEffect, useRef } from "react";
import ReactQuill from "react-quill";
import { useLocation, useNavigate } from "react-router-dom";
import { useUser } from "../../../context/UserContext";
import {
  FaUserTie,
  FaClipboardList,
  FaRegCalendarAlt,
  FaDollarSign,
  FaFileAlt,
} from "react-icons/fa";
import { LuClipboardList } from "react-icons/lu";
import SuccessModal from "../profile/SuccessModal";
import config from "../../../config/config";
import axios from "axios";

const AgreementSetupComponent = () => {
  const { workerUser } = useUser();
  const serviceRates = workerUser?.serviceRates || {};
  const initialChecked = Object.fromEntries(
    Object.entries(serviceRates)
      .filter(([day]) => day !== "Initial greeting session")
      .map(([day, rate]) => [day, Boolean(rate)])
  );

  const initialRates = Object.fromEntries(
    Object.entries(serviceRates)
      .filter(([day]) => day !== "Initial greeting session")
      .map(([day, rate]) => [day, rate])
  );
  const { state: agreementDetails } = useLocation();
  const travelRateInputRef = useRef(null);
  const {
    messageId,
    workerId,
    clientId,
    clientName,
    createdBy,
    jobId,
    jobFrequency,
    sessions,
    hoursPerWeek,
    selectedJobStartDate,
    sessionStartDate,
    sessionEndDate,
    workerName,
    jobLocation,
    jobTitle,
    jobDescription,
    totalAgreementHours,
    rates,
    offeredRateTypes,
    preferredDays = {},
    timeSlots = {},
    noFeeForInitialGreetingSession,
    initialGreetingSessionRate,
    includeTravelRate: initialIncludeTravelRate,
    travelRatePerMile: initialTravelRatePerMile,
    editorContent: initialEditorContent,
    workerSigned,
    clientSigned,
    status,
  } = agreementDetails;

  const [includeTravelRate, setIncludeTravelRate] = useState(initialIncludeTravelRate);
  const [travelRatePerMile, setTravelRatePerMile] = useState(initialTravelRatePerMile);
  const [editorContent, setEditorContent] = useState(initialEditorContent);
  const [errorMessage, setErrorMessage] = useState("");
  const [validationMessages, setValidationMessages] = useState({
    "Travel Rate": ""
  });

  const [showModal, setShowModal] = useState(false);

  const navigate = useNavigate();


  const handleReviewAndContinue = () => {
console.log("🚀 Navigating to review with agreementDetails:", agreementDetails);
    navigate("/account/ps-dashboard/agreement-review", {
      state: {
      ...agreementDetails,
      includeTravelRate,
    travelRatePerMile,
      editorContent,
    },
    });
  };

  const handleTravelCheckboxChange = () => {
    setIncludeTravelRate((prev) => !prev);
    if (!includeTravelRate) {
      setTimeout(() => {
        if (travelRateInputRef.current) {
          travelRateInputRef.current.focus();
        }
      }, 0);
    }
  };

  return (
    <div className="agreement-setup-component-container">
      <h2>Agreement Setup</h2>
      <div className="agreement-job-details">
        <h3>
          <FaUserTie className="icon-common icon-user" />
          Parties to the Agreement
        </h3>
        <p>
          This agreement is presented to <strong>{clientName}</strong>&nbsp;for the
          job outlined below.
        </p>
        <div className="agreement-travel">
          <h3>
            <LuClipboardList className="icon-common icon-list" />
            Job details
          </h3>

          <p>
            Title <span className="user-dashboard-arrow">➤</span>
            <strong>
              <span>{jobTitle}</span>
            </strong>
          </p>
          <p>
            Location <span className="user-dashboard-arrow">➤</span>
            <strong>
              <span>{jobLocation}</span>
            </strong>
          </p>
          <p>
            Job Frequency <span className="user-dashboard-arrow">➤</span>
            <strong>
              <span>{jobFrequency}</span>
            </strong>
          </p>
          <p>
            Sessions / Week <span className="user-dashboard-arrow">➤</span>
            <strong>
              <span>{sessions}</span>&nbsp;
            </strong>
          </p>
          <p>
            Hours / Session<span className="user-dashboard-arrow">➤</span>
            <strong>
              <span>{hoursPerWeek}</span>&nbsp;
            </strong>
          </p>
          <p>
            Start Date <span className="user-dashboard-arrow">➤</span>
            <strong>
              <span>{selectedJobStartDate}</span>
            </strong>
          </p>
        </div>
        <div className="agreement-travel">
          <h3><LuClipboardList className="icon-common icon-list" />
            Job Description</h3>

          <p>{jobDescription}</p>
          <p>
            Job ID <span className="user-dashboard-arrow">➤</span>
            <strong>
              <span>{jobId}</span>
            </strong>
          </p>
        </div>
      </div>

      <div className="agreement-travel">
        <h3><FaRegCalendarAlt className="icon-common icon-list" /> Agreement Days & Time Slots</h3>
        <p>Client’s requested support schedule below:</p>
      </div>
      <div className="agreement-rates-display">
        {Object.entries(preferredDays)
          .filter(([_, isPreferred]) => isPreferred)
          .map(([day]) => (
            <div key={day} className="rate-item">
              <strong>{day}<span className="user-dashboard-arrow">➤</span></strong>{" "}
              {(timeSlots[day] || []).map((slot, i) => (
                <span key={i} className="timeslot-span">
                  {slot.from} - {slot.to}
                  {i < timeSlots[day].length - 1 ? ", " : ""}
                </span>
              ))}
            </div>
          ))}
        {Object.values(preferredDays).every(v => !v) && (
          <p>No preferred days selected.</p>
        )}
      </div>

      <div className="agreement-travel">
        <p>
          Total Agreement Hours <span className="user-dashboard-arrow">➤</span><span><strong>{totalAgreementHours}</strong></span>
        </p>
      </div>
      <div className="agreement-travel">
        <h3><FaDollarSign className="icon-common icon-dollar" />
          Your Post–Meet & Greet Session Rates</h3>
        <p>Rates outlined in this agreement include our <strong>7%</strong> One Support Hub platform fee.</p>
      </div>
      <div className="agreement-rates-display">
        {Object.entries(rates)
          .filter(([type]) => offeredRateTypes.includes(type))
          .map(([type, rate]) => (
            <div key={type} className="rate-item">
              {type}<span className="user-dashboard-arrow">➤</span>{" "}
              <strong>${rate}</strong>
            </div>
          ))}

        <div className="rate-item">
          Meet & Greet Session rate<span className="user-dashboard-arrow">➤</span>
          <strong>{noFeeForInitialGreetingSession
            ? 'Free'
            : `$${initialGreetingSessionRate}`}</strong>
        </div>
      </div>
      <div className="agreement-timeslot-availability">
      </div>
      <div className="agreement-travel">
        <h3><FaDollarSign className="icon-common icon-dollar" />
          Travel Cost</h3>
        <p>
          Your client must agree to add a per kilometre rate for services. Talk
          to them before you add this to your agreement.
        </p>
        <div className="initial-greeting">
          <label className="meet-and-greet">
            <input
              type="checkbox"
              checked={includeTravelRate}
              onChange={handleTravelCheckboxChange}
            />
            Include travel rate per Mile
          </label>
          {includeTravelRate && (
            <div className="travel-rate-entry">
              <div className="rate-input-wrapper">
                <span className="rate-dollar-sign">$</span>
                <input
                  type="number"
                  value={travelRatePerMile}
                  onChange={(e) => setTravelRatePerMile(e.target.value)}
                  className={
                    validationMessages["Travel Rate"] ? "invalid-rate" : ""
                  }
                  ref={travelRateInputRef}
                />
              </div>
            </div>
          )}
        </div>
      </div>
      <div className="agreement-travel">
        <h3><LuClipboardList className="icon-common icon-list" />
          Action Items for Finalization</h3>
        <p>
          If there any additional considerations discussed with the client that
          need to be added to this agreement, Please address them below.
        </p>

        <ReactQuill
          value={editorContent}
          onChange={setEditorContent}
          modules={{
            toolbar: [
              [{ header: "1" }, { header: "2" }, { font: [] }],
              [{ list: "ordered" }, { list: "bullet" }],
              ["bold", "italic", "underline"],
              [{ color: [] }, { background: [] }],
              ["clean"],
            ],
          }}
        />

        <div className="bio-indicator">
          <span className="warning">Minimum 200 characters required!</span>
        </div>
        {errorMessage && <div className="error-message">{errorMessage}</div>}
        <div className="rate-indicative-save">
          <button onClick={handleReviewAndContinue}>Continue</button>
        </div>
      </div>
    </div>
  );
};

export default AgreementSetupComponent;
